import {
  DOCS_FORM_NAMES,
  EARLY_REHAB_CRITERIA,
  GENDER,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import { getUnixTime } from "date-fns";
import {
  ControlledCheckboxGroup,
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
  ControlledTextarea,
} from "ds/ui";
import Translations from "translations/types";
import { FormFieldConfig } from "..";
import {
  ComplicationInTreatmentProcessField,
  ICDCodeFields,
  InfectionsStateField,
  RecommendedRehaTypeField,
  RehabAdmissionTypeField,
  RehabDirectiveStateField,
  RehabFocusField,
  RehaCapacityField,
  SpecialRequirementsRequiredField,
  SurgeryPerformedField,
  TransportTypeField,
} from "./fields";

const getGenderOptions = (translations: Translations) => {
  return [
    {
      label: translations.patientForms.medicalForm.sectionOne.genderMale,
      value: GENDER.MALE,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.genderFemale,
      value: GENDER.FEMALE,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.genderDiverse,
      value: GENDER.DIVERSE,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionTwo.unknown,
      value: GENDER.UNSPECIFIED,
    },
  ];
};

const getEarlyRehabCriteriaOptions = (translations: Translations) => {
  return [
    {
      label:
        translations.patientForms.medicalForm.sectionThree
          .intensiveCareMonitoring,
      value: EARLY_REHAB_CRITERIA.INTENSIVE_CARE_MONITORING,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree
          .intermittentVentilation,
      value: EARLY_REHAB_CRITERIA.INTERMITTENT_VENTILATION,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree.behaviouralDisorder,
      value: EARLY_REHAB_CRITERIA.BEHAVIOURAL_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree
          .communicationDisorder,
      value: EARLY_REHAB_CRITERIA.COMMUNICATION_DISORDER,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.tracheostoma,
      value: EARLY_REHAB_CRITERIA.TRACHEOSTOMA,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree.orientationDisorder,
      value: EARLY_REHAB_CRITERIA.ORIENTATION_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionThree.swallowingDisorder,
      value: EARLY_REHAB_CRITERIA.SWALLOWING_DISORDER,
    },
  ];
};

const getDoctorAuthorizationOptions = (translations: Translations) => {
  return [
    {
      label:
        translations.insuranceApp.eligibilityTab.approvalFlow
          .authorisationOptionOne,
      value: 1,
    },
    {
      label:
        translations.insuranceApp.eligibilityTab.approvalFlow
          .authorisationQuestionTwo,
      value: 2,
    },
  ];
};

export const GKVMedicalFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.GKV_MEDICAL
>["sections"] = [
  // Section One: Patient Information
  {
    fields: {
      admission_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDateInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionOne.intakeDate}
            name="admission_date"
          />
        ),
        position: 5,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionOne
            .hospitalStayInformation,
      },
      discharge_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDateInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionOne.dischargeDate
            }
            name="discharge_date"
          />
        ),
        position: 6,
      },
      early_rehabilitation_measures: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionOne
                  .earlyRehabMeasures
              }
              name="early_rehabilitation_measures"
              options={options}
            />
          );
        },
        position: 7,
      },
      gender: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = getGenderOptions(translations);
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={translations.patientForms.drvGeneralForm.sectionTwo.gender}
              name="gender"
              options={options}
            />
          );
        },
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionOne
            .informationInsuredPerson,
      },
      insurance_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson
                .insuranceNumber
            }
            name="insurance_number"
            placeholder=" "
          />
        ),
        position: 4,
      },
      patient_birthdate: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDateInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson.dateOfBirth
            }
            maxValue={getUnixTime(new Date())}
            name="patient_birthdate"
          />
        ),
        position: 3,
      },
      patient_first_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionOne.firstName}
            name="patient_first_name"
            placeholder=" "
          />
        ),
        position: 1,
      },
      patient_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionOne.lastName}
            name="patient_last_name"
            placeholder=" "
          />
        ),
        position: 2,
      },
      rehab_admission_type: {
        input: RehabAdmissionTypeField,
        position: 8,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionOne.admissionRehab +
          " *",
      },
    },
    position: 1,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionOne.title,
  },
  // Section Two: Function Diagnoses
  {
    fields: {
      complication_in_treatment_process: {
        input: ComplicationInTreatmentProcessField,
        position: 4,
      },
      icd_code_1_diagnosis: {
        hideSource: true,
        input: ICDCodeFields,
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionTwo.functionDiagnoses,
      },
      infections_state: {
        input: InfectionsStateField,
        position: 5,
      },
      other_treatments: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionTwo.otherTreatments
            }
            name="other_treatments"
          />
        ),
        position: 3,
      },
      surgery_performed: {
        input: SurgeryPerformedField,
        position: 2,
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionTwo.title,
  },
  // Section Three: Rehabilitation Capacity
  {
    fields: {
      interim_prosthesis_fitted: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionThree.prosthesis
              }
              name="interim_prosthesis_fitted"
              options={options}
            />
          );
        },
        position: 2,
      },
      limited_capacity: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroup
              aria-label={
                translations.patientForms.medicalForm.sectionThree.resilience
              }
              isRequired={!skipRequiredValidation}
              name="limited_capacity"
              options={options}
            />
          );
        },
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionThree.resilience + " *",
      },
      reha_capacity: {
        input: RehaCapacityField,
        position: 3,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionThree
            .musculoskeletalSystem,
      },
      reha_capacity_early_rehab_criteria: {
        input: ({ translations }) => {
          const options = getEarlyRehabCriteriaOptions(translations);
          return (
            <ControlledCheckboxGroup
              aria-label={
                translations.patientForms.medicalForm.sectionThree
                  .earlyRehabIndex
              }
              name="reha_capacity_early_rehab_criteria"
              options={options}
              size="md"
            />
          );
        },
        position: 5,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionThree.earlyRehabIndex,
      },
      reha_capacity_other: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={translations.patientForms.medicalForm.sectionThree.specifics}
            name="reha_capacity_other"
          />
        ),
        position: 4,
      },
    },
    position: 3,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionThree.title,
  },
  // Section Four: Rehabilitation Goals
  {
    fields: {
      rehabilitation_goals: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledTextarea
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionFour.rehabGoals}
            name="rehabilitation_goals"
          />
        ),
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionFour.rehabGoals + " *",
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionFour.title,
  },
  // Section Five: Rehabilitation Focus
  {
    fields: {
      recommended_reha_type: {
        input: RecommendedRehaTypeField,
        position: 2,
      },
      rehab_directive_state: {
        input: RehabDirectiveStateField,
        position: 3,
      },
      rehab_focus: {
        input: RehabFocusField,
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionFive.rehabFocus + " *",
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionFive.title,
  },
  // Section Six: Special Requirements and Transport
  {
    fields: {
      approved_by: {
        input: ({ skipRequiredValidation, translations }) => {
          if (skipRequiredValidation) return null;
          const options = getDoctorAuthorizationOptions(translations);
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.insuranceApp.eligibilityTab.approvalFlow
                  .authorisationQuestionTitle
              }
              name="approved_by"
              options={options}
            />
          );
        },
        position: 7,
      },
      doctor_exam_date: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDateInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionSix
                .doctorExamDateLabel
            }
            name="doctor_exam_date"
          />
        ),
        position: 8,
      },
      doctor_in_charge_in_hospital_first_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.firstName}
            name="doctor_in_charge_in_hospital_first_name"
            placeholder=" "
          />
        ),
        position: 4,
        subTitle: ({ translations }) =>
          translations.patientForms.medicalForm.sectionSix
            .contactInformationDoctor,
      },
      doctor_in_charge_in_hospital_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.lastName}
            name="doctor_in_charge_in_hospital_last_name"
            placeholder=" "
          />
        ),
        position: 5,
      },
      doctor_in_charge_in_hospital_phone: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionSix.phoneDoctor}
            name="doctor_in_charge_in_hospital_phone"
            placeholder=" "
            type="tel"
          />
        ),
        position: 6,
      },
      require_accompanying_person: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionSix
                  .accompanyingPerson
              }
              name="require_accompanying_person"
              options={options}
            />
          );
        },
        position: 3,
      },
      special_requirements_required: {
        input: SpecialRequirementsRequiredField,
        position: 1,
      },
      transport_type: {
        input: TransportTypeField,
        position: 2,
      },
    },
    position: 6,
    title: ({ translations }) =>
      translations.patientForms.medicalForm.sectionSix.title,
  },
];
